import { Button, Col, Flex, Grid, Layout, Row, theme, Typography } from 'antd';
import clsx from 'clsx';
import { ReactComponent as GrayLeavesDecor } from 'assets/decor/GrayLeaves.svg';
import { ReactComponent as GreenPlantDecor } from 'assets/decor/GreenPlant.svg';
import { useAuthContext } from 'contexts/AuthContext';
import { NavigationItemInterface } from 'interfaces/navigationItem.interface';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import {
  getBottomNavigationItems,
  getSidebarNavigationItems,
} from 'routes/navigationConfig';
import { CalculetteModalProvider } from 'contexts/CalculetteModalContext';
import { useCurrentSimulations } from 'contexts/SimulationsContext';
import { useLeadEmailLanding } from 'hooks/leadEmailLanding.hooks';
import { useSidebarCollapsed } from 'hooks/useSidebarCollapsed';
import AppHeader from 'layouts/Component/AppHeader';
import CalculetteModalHost from 'layouts/Component/CalculetteModalHost';
import Sidebar, {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from 'layouts/Component/Sidebar';

const { Content, Footer } = Layout;

const IMPERSONATION_BAR_HEIGHT = 52;

/** « Mon compte » liste déjà les liens légaux : le pied de page ferait doublon. */
const ROUTES_WITHOUT_FOOTER = ['/mon-compte'];

const FooterList = (props: { items: Array<React.ReactNode> }) => {
  const { items } = props;
  return (
    <Col xs={24} md={12} lg={6}>
      <ul>
        {items.map((item: React.ReactNode, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <li key={`item${index}`}>{item}</li>
        ))}
      </ul>
    </Col>
  );
};

const FooterLinkList = (props: { items: NavigationItemInterface[] }) => {
  const { items } = props;
  return (
    <FooterList
      items={items.map(({ key, label, target = '_self' }) => (
        <a key={key} href={key} target={target}>
          {label}
        </a>
      ))}
    />
  );
};

const AppLayout = (props: { children: React.ReactNode }) => {
  const { children } = props;
  const { auth, logout, owner, isLeadReferent, isLeadOnly } = useAuthContext();
  const { t } = useTranslation();
  const history = useHistory();
  const { removeAllSimulations } = useCurrentSimulations();
  const { isLandingFromEmail } = useLeadEmailLanding();
  const { collapsed, toggle } = useSidebarCollapsed();
  const { pathname } = useLocation();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const showFooter = !ROUTES_WITHOUT_FOOTER.includes(pathname);

  // Coordonnées du propriétaire du contact
  const ownerEmail: string | undefined = owner?.Owner?.Email;
  const ownerTel: string | undefined = owner?.Owner?.MobilePhone;

  const displayName = `${auth?.user?.firstname} ${auth?.user?.lastname}`;

  const handleLogout = useCallback(() => {
    removeAllSimulations();
    logout();
    history.push('/connexion');
  }, [history, logout, removeAllSimulations]);

  const sidebarItems = useMemo(
    () => getSidebarNavigationItems(isLeadReferent, isLeadOnly),
    [isLeadReferent, isLeadOnly],
  );

  const bottomNavigationItems = useMemo(
    () => getBottomNavigationItems(isLeadReferent, isLeadOnly),
    [isLeadReferent, isLeadOnly],
  );

  const {
    token: {
      Layout: { maxWidth, headerHeight },
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = theme.useToken() as any;

  const isImpersonated = auth?.user?.impersonated;
  const sidebarWidth = collapsed
    ? SIDEBAR_WIDTH_COLLAPSED
    : SIDEBAR_WIDTH_EXPANDED;
  const topOffset =
    headerHeight + (isImpersonated ? IMPERSONATION_BAR_HEIGHT : 0);

  if (isLandingFromEmail && isMobile) {
    return (
      <Layout
        className="print:bg-white"
        style={{ minHeight: '100vh', overflowX: 'hidden', maxWidth: '100vw' }}
      >
        <Content
          className="flex print:p-0 px-4 md:px-[32px] pt-6 md:pt-[32px] pb-[132px] print:mt-0"
          style={{ position: 'relative', marginTop: 0 }}
        >
          <Flex vertical className="grow w-full mx-auto" style={{ maxWidth }}>
            {children}
          </Flex>
        </Content>
      </Layout>
    );
  }

  return (
    <CalculetteModalProvider>
      <Layout
        className="print:bg-white"
        style={{
          minHeight: '100vh',
        }}
      >
        {isImpersonated && (
          <div
            className={
              'print:hidden fixed top-0 left-0 right-0 bg-gray-800 flex items-center justify-center gap-6 h-[52px] z-[1000]'
            }
          >
            <Typography.Paragraph className="!m-0 text-white">
              {t('login:impersonate', {
                name: displayName,
              })}
            </Typography.Paragraph>
            <Button onClick={handleLogout} type="primary">
              {t('global:logout')}
            </Button>
          </div>
        )}

        <AppHeader
          onLogout={handleLogout}
          isImpersonated={Boolean(isImpersonated)}
        />

        {/* Le Sider doit rester dans un Layout dédié : AntD passe en
            `flex-direction: row` tout Layout qui a un Sider pour enfant. */}
        <Layout hasSider className="print:bg-white">
          <Sidebar
            items={sidebarItems}
            collapsed={collapsed}
            onToggle={toggle}
            topOffset={topOffset}
          />

          <Layout
            className="print:bg-white print:!ml-0"
            style={{
              marginLeft: sidebarWidth,
              transition: 'margin-left 0.2s',
            }}
          >
            <Content
              className={clsx(
                'flex print:p-0 px-[32px] pt-[32px] print:mt-0',
                // Le retrait bas dégage le décor végétal du pied de page ; sans
                // pied de page il ne laisserait qu'un vide.
                showFooter ? 'pb-[132px]' : 'pb-[32px]',
              )}
              style={{
                position: 'relative',
                marginTop: topOffset,
              }}
            >
              <Flex
                vertical
                className="grow w-full mx-auto"
                style={{ maxWidth }}
              >
                {children}
              </Flex>
            </Content>

            {showFooter && (
              <Footer className="print:hidden">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth,
                    margin: 'auto',
                    position: 'relative',
                  }}
                >
                  <div
                    className="flex"
                    style={{
                      position: 'absolute',
                      top: '-123px',
                      right: 0,
                    }}
                  >
                    <GreenPlantDecor style={{ verticalAlign: 'middle' }} />
                    <GrayLeavesDecor style={{ verticalAlign: 'middle' }} />
                  </div>
                  <Row
                    justify="space-between"
                    style={{ width: '100%' }}
                    gutter={16}
                  >
                    <FooterList items={[]} />
                    <FooterList
                      items={[
                        <React.Fragment key="email">
                          <a href={`mailto:${ownerEmail}`}>{ownerEmail}</a>
                        </React.Fragment>,
                        <React.Fragment key="tel">
                          <a href={`tel:${ownerTel}`}>{ownerTel}</a>
                        </React.Fragment>,
                      ]}
                    />
                    <FooterLinkList
                      items={bottomNavigationItems.slice(
                        0,
                        Math.ceil(bottomNavigationItems.length / 2),
                      )}
                    />
                    <FooterLinkList
                      items={bottomNavigationItems.slice(
                        Math.ceil(bottomNavigationItems.length / 2),
                      )}
                    />
                  </Row>
                </div>
              </Footer>
            )}
          </Layout>
        </Layout>

        {!isLeadOnly && <CalculetteModalHost />}
      </Layout>
    </CalculetteModalProvider>
  );
};

export default React.memo(AppLayout);
